import React, { useState } from "react";

const TIMEZONES = [
  // Most used or requested on top
  "Europe/Paris",
  "Europe/London",
  // Europe
  "Europe/Amsterdam",
  "Europe/Andorra",
  "Europe/Astrakhan",
  "Europe/Athens",
  "Europe/Belgrade",
  "Europe/Berlin",
  "Europe/Bratislava",
  "Europe/Brussels",
  "Europe/Bucharest",
  "Europe/Budapest",
  "Europe/Chisinau",
  "Europe/Copenhagen",
  "Europe/Dublin",
  "Europe/Gibraltar",
  "Europe/Helsinki",
  "Europe/Istanbul",
  "Europe/Kaliningrad",
  "Europe/Kiev",
  "Europe/Kirov",
  "Europe/Lisbon",
  "Europe/Ljubljana",
  "Europe/Luxembourg",
  "Europe/Madrid",
  "Europe/Malta",
  "Europe/Mariehamn",
  "Europe/Minsk",
  "Europe/Monaco",
  "Europe/Moscow",
  "Europe/Oslo",
  "Europe/Podgorica",
  "Europe/Prague",
  "Europe/Riga",
  "Europe/Rome",
  "Europe/Samara",
  "Europe/San_Marino",
  "Europe/Sarajevo",
  "Europe/Saratov",
  "Europe/Simferopol",
  "Europe/Skopje",
  "Europe/Sofia",
  "Europe/Stockholm",
  "Europe/Tallinn",
  "Europe/Tirane",
  "Europe/Ulyanovsk",
  "Europe/Uzhgorod",
  "Europe/Vaduz",
  "Europe/Vatican",
  "Europe/Vienna",
  "Europe/Vilnius",
  "Europe/Volgograd",
  "Europe/Warsaw",
  "Europe/Zagreb",
  "Europe/Zaporozhye",
  "Europe/Zurich",
  // America
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Halifax",
  "America/Phoenix",
  "America/Argentina/Buenos_Aires",
  "America/Sao_Paulo",
  "America/Toronto",
  "America/Vancouver",
  // Asia
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Singapore",
  "Asia/Seoul",
  "Asia/Bangkok",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Jakarta",
  "Asia/Manila",
  // Australia
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Perth",
  "Australia/Adelaide",
  // Africa
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "Africa/Nairobi",
  // Pacific
  "Pacific/Auckland",
  "Pacific/Honolulu",
  "Pacific/Fiji",
  // UTC
  "UTC"
];

export default function CalendarModal({
  open,
  onClose,
  onSave,
  initial,
  loading,
  error,
  userCompanies = [], // New prop for user's companies
}) {
  const [name, setName] = useState(initial?.name || "");
  const [timezone, setTimezone] = useState(initial?.timezone || "Europe/Amsterdam");
  const [selectedCompanyId, setSelectedCompanyId] = useState(initial?.companyId || "");

  // Reset fields when modal opens
  React.useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setTimezone(initial?.timezone || "Europe/Amsterdam");
      setSelectedCompanyId(initial?.companyId || (userCompanies.length > 0 ? userCompanies[0].id : ""));
    }
  }, [open, initial, userCompanies]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl p-6 w-80 relative">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{initial ? "Kalender bewerken" : "Nieuwe kalender"}</h2>
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            aria-label="Sluit"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>
        {error && <div className="text-red-500 text-xs mb-2">{error}</div>}
        <div className="mb-3">
          <label className="block text-xs text-gray-500 mb-1">Naam</label>
          <input
            className="input-field"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={loading}
            autoFocus
          />
        </div>
        <div className="mb-3">
          <label className="block text-xs text-gray-500 mb-1">Bedrijf *</label>
          <select
            className="input-field"
            value={selectedCompanyId}
            onChange={e => setSelectedCompanyId(e.target.value)}
            disabled={loading || userCompanies.length === 0}
          >
            <option value="">Selecteer een bedrijf...</option>
            {userCompanies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
          {userCompanies.length === 0 && (
            <div className="text-xs text-red-500 mt-1">
              U bent nog niet aan een bedrijf gekoppeld. Neem contact op met een beheerder.
            </div>
          )}
        </div>
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-1">Tijdzone</label>
          <select
            className="input-field"
            value={timezone}
            onChange={e => setTimezone(e.target.value)}
            disabled={loading}
          >
            {TIMEZONES.map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
        {initial && initial.id && (
          <div className="mb-4 text-xs bg-blue-50 p-2 rounded-lg">
            <div className="mb-1 font-semibold">Huidige on-call nummer:</div>
            <div className="mb-1">
              Via deze <a
                href={`${window.env.API_URL}/api/v1/events/current-on-call/${initial.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline"
              >link</a> kan je programmatorisch het huidige on-call nummer voor deze agenda opvragen.<br/>
              Dit geeft het telefoonnummer van de persoon die nu on-call is, of niets als er geen persoon is ingevuld.
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={loading}
          >Annuleer</button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => onSave({ name, timezone, companyId: selectedCompanyId })}
            disabled={loading || !name.trim() || !selectedCompanyId}
          >Opslaan</button>
        </div>
      </div>
    </div>
  );
}
